import { PreferenceType } from "components/preferences";
import { usePreference } from "hooks/preferences";
import { useGridController, useGridMetadata, useRefresh } from "hooks/shared";
import { IWorkItemContext, useUnrelateWorkItem, useWorkItemContext } from "hooks/work-items";
import useRelatedWorkItems from "hooks/work-items/useRelatedWorkItems";
import { GridMetaData, Sort } from "models/grids";
import useRelatedWorkItemsColumns from "./useRelatedWorkItemsColumns";
import { IRelatedWorkItem } from "models/work-items";
import { Box, Button, CircularProgress, Grid, styled } from "@mui/material";
import { CentralDataGridPro, GridBox, GridToolbar } from "components/shared/grids";
import { GridOverlay, useGridApiRef } from "@mui/x-data-grid-pro";
import { Ico_Filter_Active_Component, Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending } from "@sagecentral/core-ui";
import { CustomCircularProgress } from "components/shared/layout";
import AddRelatedWorkItemDrawer from "./AddRelatedWorkItemDrawer";
import useWorkItemDraftContext, { IWorkItemDraftContext } from "hooks/work-items/useWorkItemDraftContext";
import useWorkItemDraftFieldUpdater from "hooks/work-items/useWorkItemDraftFieldUpdater";
import { useEffect, useReducer } from "react";
import { customLocaleText } from "models/grids/HeaderFilterLocaleText";


const GridNoRowsOverlay = () => {
    return (
        <GridOverlay>
            <Box>No related work items found</Box>
        </GridOverlay>
    )
};

const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 'bold'
});

const DefaultSort: Sort = {
    field: 'workItemId',
    dir: 'asc'
};

type RelatedWorkItemsGridPageProps = {
    draft?: boolean;
};

const RelatedWorkItemsGrid = (props: RelatedWorkItemsGridPageProps) => {    
    const { draft } = props;
    const draftContext = useWorkItemDraftContext();
    const workItemContext = useWorkItemContext();
    const workItemId = draft && draftContext.workItem?.id ? draftContext.workItem.id : workItemContext.workItem?.id ?? 0;
    const [ setVisitedRelated, loadingVisited ] = useWorkItemDraftFieldUpdater('related');
    const [ refreshIndex, refresh ] = useRefresh();
    const [ preferencesLoaded, settings, setSettings ] = usePreference(PreferenceType.WorkItemRelatedWorkItems, false);

    const [ unrelate, UnrelateDialog ] = useUnrelateWorkItem(workItemId, refresh, draft);

    const [ columns, visibilityModel, handleColumnVisibilityOrOrderChange ] = useRelatedWorkItemsColumns({
        unrelate,
        settings,
        setSettings
    });

    const gridController = useGridController({
        defaultSort: [ DefaultSort ],
        visibilityModel,
        onColumnChange: handleColumnVisibilityOrOrderChange,
        settings,
        setSettings
    });

    const {
        filterModel,
        onFilterChange,
        sortModel,
        onSortChange,
        density,
        onDensityChange,
        pagination,
        onPaginationChange,
        isFilterApplied,
        onColumnChange,
        filter,
        sort
    } = gridController;

    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);
    const controller = useRelatedWorkItems(workItemId, gridMetadata, refresh, refreshIndex, draft);
    
    const apiRef = useGridApiRef();

    const loading = controller?.loading ?? true;
    const values = controller?.values ?? [];
    const count = controller?.count ?? 0;
    
    const getRows: () => IRelatedWorkItem[] = () => loading ? [] : values;
    
    return (
        <>
        {
            draft &&
            <FormTitle>
                Related Work Items
            </FormTitle>
        }
        { UnrelateDialog }
        { workItemId > 0 &&
            <Grid container sx={{ minWidth: '100%' }}>
                <AddRelatedWorkItemDrawer 
                    workItemId={workItemId} 
                    draft={draft} 
                    selectedWorkspaceId={draft ? draftContext.workItem?.workspaceId : workItemContext.workItem?.workspaceId}
                    refresh={refresh}
                />
                <Grid item xs={12} sx={{ mt: '8px' }}>
                    <GridBox getData={getRows} minRowHeight={52}>
                            <Box sx={{ height: '55vh'}}>
                                <CentralDataGridPro
                                    apiRef={apiRef}
                                    loading={loading}
                                    rows={values}                
                                    rowCount={count}
                                    columns={columns}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: visibilityModel
                                        }
                                    }}
                                    columnBufferPx={40 / 8}
                                    rowHeight={52}
                                    getRowId={(r) => r.id}
                                    getRowHeight={() =>'auto'}
                                    filterMode="server"
                                    filterModel={filterModel}
                                    sortingMode="server"
                                    sortModel={sortModel}
                                    paginationMode='server'
                                    pagination={true}
                                    pageSizeOptions={[25, 50, 75, 100]}
                                    paginationModel={pagination}
                                    density={density ?? 'standard'}
                                    onColumnVisibilityModelChange={() => onColumnChange?.(apiRef)}
                                    onColumnWidthChange={() => onColumnChange?.(apiRef)}
                                    onColumnOrderChange={() => onColumnChange?.(apiRef)}
                                    onFilterModelChange={onFilterChange}
                                    onDensityChange={(newDensity) => onDensityChange(newDensity)}
                                    onPaginationModelChange={onPaginationChange}
                                    onSortModelChange={onSortChange}
                                    slots={{
                                        loadingOverlay: () => (<CircularProgress/>),
                                        columnMenuSortAscendingIcon: Ico_SortAscending,
                                        columnMenuSortDescendingIcon: Ico_SortDescending,
                                        columnSortedAscendingIcon: Ico_SortAscending,
                                        columnSortedDescendingIcon: Ico_SortDescending,
                                        columnUnsortedIcon: undefined,
                                        openFilterButtonIcon: isFilterApplied?.() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                                        quickFilterIcon: Ico_Filter_Component,
                                        columnFilteredIcon: Ico_Filter_Component,
                                        quickFilterClearIcon: Ico_Filter_Component,
                                        columnMenuFilterIcon: Ico_Filter_Component,
                                        noRowsOverlay: GridNoRowsOverlay,
                                        toolbar: GridToolbar
                                    }}
                                    headerFilters
                                    localeText={customLocaleText}
                                    getRowClassName={(params) =>
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                                    }
                                />
                            </Box>
                    </GridBox>
                </Grid>
            </Grid>
        }
        </>
    )
};

export default RelatedWorkItemsGrid;